import React, { useEffect, useState, useMemo } from 'react';
import { CHAPTER_IDS } from '@shared/chapters';
import {
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Loader2,
  RefreshCw,
  HelpCircle,
  Globe,
  Image as ImageIcon,
  CheckCircle,
  AlertTriangle,
  Eye,
  Edit3,
  X,
  EyeOff,
} from 'lucide-react';
import { adminFetch } from '../../lib/api';
import { QAStatusBadge, TranslationDot, ChangeMarkerBadge } from './QAStatusBadge';
import type { QAStatus } from './QAStatusBadge';

const CHAPTERS = CHAPTER_IDS;
const DIFFICULTIES = ['Leicht', 'Mittel', 'Schwer'];
const TYPES = ['Single Choice', 'Multiple Choice', 'Situation'];
const QA_STATUSES: { value: QAStatus | ''; label: string }[] = [
  { value: '', label: 'Alle Status' },
  { value: 'draft', label: '📝 Entwurf' },
  { value: 'in_progress', label: '🔵 In Bearbeitung' },
  { value: 'in_review', label: '🟡 In Prüfung' },
  { value: 'updated', label: '🟠 Aktualisiert' },
  { value: 'approved', label: '🟢 Freigegeben' },
  { value: 'live', label: '✅ Live' },
  { value: 'archived', label: '📦 Archiviert' },
];

const LANG_FLAGS: Record<string, string> = {
  en: '🇬🇧',
  tr: '🇹🇷',
  ar: '🇸🇦',
  prs: '🇦🇫',
  ur: '🇵🇰',
  uk: '🇺🇦',
};

interface Question {
  id: string;
  chapter_id: number;
  difficulty: string;
  type: string;
  points: number;
  question_text_de: string;
  qa_status: QAStatus;
  change_markers: string[];
  flags: string[];
  has_image: boolean;
  image_hidden: boolean;
  translations: Record<string, string>;
  translation_progress: string;
  last_reviewed_at: string | null;
  last_approved_at: string | null;
}

type SortKey = 'chapter_id' | 'difficulty' | 'qa_status' | 'question_text_de';

export const QAQuestionList: React.FC<{
  onSelectQuestion: (id: string) => void;
}> = ({ onSelectQuestion }) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterChapter, setFilterChapter] = useState<number | ''>('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState<QAStatus | ''>('');
  const [sortKey, setSortKey] = useState<SortKey>('chapter_id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [filterImageHidden, setFilterImageHidden] = useState(false);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (filterChapter) params.set('chapter', String(filterChapter));
      if (filterDifficulty) params.set('difficulty', filterDifficulty);
      if (filterType) params.set('type', filterType);
      if (searchTerm) params.set('search', searchTerm);

      const resp = await adminFetch(`/api/admin/content-qa/questions?${params}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setQuestions(data.questions || []);
    } catch (e: any) {
      setMessage({ text: `Fehler: ${e.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuestions();
  }, [filterChapter, filterDifficulty, filterType]);

  const handleSearch = () => {
    loadQuestions();
  };

  const filtered = useMemo(() => {
    let result = [...questions];
    if (filterStatus) {
      result = result.filter((q) => q.qa_status === filterStatus);
    }
    if (filterImageHidden) {
      result = result.filter((q) => q.image_hidden);
    }
    result.sort((a, b) => {
      const aVal = a[sortKey] ?? '';
      const bVal = b[sortKey] ?? '';
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [questions, filterStatus, filterImageHidden, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((q) => q.id)));
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    if (!confirm(`${selectedIds.size} Fragen: "${bulkAction}" ausführen?`)) return;
    setBulkProcessing(true);
    try {
      const resp = await adminFetch('/api/admin/content-qa/bulk', {
        method: 'POST',
        body: JSON.stringify({ action: bulkAction, question_ids: Array.from(selectedIds) }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setMessage({ text: `${data.processed}/${data.total} Fragen verarbeitet.`, type: 'success' });
      setSelectedIds(new Set());
      setBulkAction('');
      await loadQuestions();
    } catch (e: any) {
      setMessage({ text: `Fehler: ${e.message}`, type: 'error' });
    } finally {
      setBulkProcessing(false);
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return null;
    return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  // Status summary counts
  const statusSummary = useMemo(() => {
    const counts: Record<string, number> = {};
    questions.forEach((q) => {
      counts[q.qa_status] = (counts[q.qa_status] || 0) + 1;
    });
    return counts;
  }, [questions]);

  const hiddenImageCount = useMemo(
    () => questions.filter((q) => q.image_hidden).length,
    [questions]
  );

  return (
    <div>
      {/* Message */}
      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} mb-4`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
          <span>{message.text}</span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setMessage(null)}
            style={{ marginLeft: 'auto' }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Status Quick Filters */}
      <div className="flex gap-2 mb-4" style={{ flexWrap: 'wrap' }}>
        {QA_STATUSES.map((s) => {
          const count = s.value ? statusSummary[s.value] || 0 : questions.length;
          const isActive = filterStatus === s.value;
          return (
            <button
              key={s.value}
              className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFilterStatus(s.value as QAStatus | '')}
              style={{ fontSize: 11, padding: '4px 10px', position: 'relative' }}
            >
              {s.label}
              {count > 0 && (
                <span
                  style={{
                    marginLeft: 4,
                    fontSize: 9,
                    fontWeight: 700,
                    background: isActive ? 'rgba(255,255,255,0.25)' : 'var(--bg-secondary)',
                    padding: '1px 5px',
                    borderRadius: 8,
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
        {/* Hidden Image filter */}
        <button
          className={`btn btn-sm ${filterImageHidden ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setFilterImageHidden(!filterImageHidden)}
          style={{ fontSize: 11, padding: '4px 10px' }}
        >
          🙈 Bild versteckt
          {hiddenImageCount > 0 && (
            <span
              style={{
                marginLeft: 4,
                fontSize: 9,
                fontWeight: 700,
                background: filterImageHidden ? 'rgba(255,255,255,0.25)' : 'var(--bg-secondary)',
                padding: '1px 5px',
                borderRadius: 8,
                color: hiddenImageCount > 0 ? '#ff6b6b' : undefined,
              }}
            >
              {hiddenImageCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters */}
      <div className="table-container">
        <div className="table-toolbar" style={{ flexWrap: 'wrap' }}>
          <div className="flex gap-3 items-center" style={{ flex: 1, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', minWidth: 220 }}>
              <Search
                size={16}
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                }}
              />
              <input
                className="input input-search"
                placeholder="Fragetext suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <select
              className="input"
              style={{ width: 140 }}
              value={filterChapter}
              onChange={(e) => setFilterChapter(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Alle Kapitel</option>
              {CHAPTERS.map((ch) => (
                <option key={ch} value={ch}>
                  Kapitel {ch}
                </option>
              ))}
            </select>
            <select
              className="input"
              style={{ width: 130 }}
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value)}
            >
              <option value="">Schwierigkeit</option>
              {DIFFICULTIES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <select
              className="input"
              style={{ width: 150 }}
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">Fragentyp</option>
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <button className="btn btn-secondary btn-sm" onClick={loadQuestions}>
              <RefreshCw size={12} />
            </button>
          </div>
          <div className="flex gap-2 items-center">
            <span className="text-xs text-muted">{filtered.length} Ergebnisse</span>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div
            style={{
              padding: '8px 16px',
              background: 'rgba(59,130,246,0.08)',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span className="text-sm" style={{ fontWeight: 600 }}>
              {selectedIds.size} ausgewählt
            </span>
            <select
              className="input"
              style={{ width: 180, fontSize: 12 }}
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
            >
              <option value="">Aktion wählen...</option>
              <option value="approve">✅ Freigeben</option>
              <option value="publish">🚀 Live schalten</option>
              <option value="set_in_review">👁️ In Prüfung setzen</option>
              <option value="archive">📦 Archivieren</option>
            </select>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleBulkAction}
              disabled={!bulkAction || bulkProcessing}
            >
              {bulkProcessing ? (
                <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
              ) : null}
              Ausführen
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setSelectedIds(new Set())}>
              Auswahl aufheben
            </button>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filtered.length && filtered.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th
                    onClick={() => handleSort('chapter_id')}
                    style={{ width: 70, cursor: 'pointer' }}
                  >
                    Kap. <SortIcon col="chapter_id" />
                  </th>
                  <th onClick={() => handleSort('question_text_de')} style={{ cursor: 'pointer' }}>
                    Fragetext <SortIcon col="question_text_de" />
                  </th>
                  <th
                    onClick={() => handleSort('difficulty')}
                    style={{ width: 100, cursor: 'pointer' }}
                  >
                    Schwierigkeit <SortIcon col="difficulty" />
                  </th>
                  <th
                    onClick={() => handleSort('qa_status')}
                    style={{ width: 130, cursor: 'pointer' }}
                  >
                    Status <SortIcon col="qa_status" />
                  </th>
                  <th style={{ width: 100 }}>Sprachen</th>
                  <th style={{ width: 50 }}>Bild</th>
                  <th style={{ width: 60 }}>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}
                    >
                      Keine Fragen gefunden.
                    </td>
                  </tr>
                ) : (
                  filtered.map((q) => (
                    <tr
                      key={q.id}
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).tagName !== 'INPUT') onSelectQuestion(q.id);
                      }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(q.id)}
                          onChange={() => toggleSelect(q.id)}
                        />
                      </td>
                      <td>
                        <span className="badge badge-neutral">{q.chapter_id}</span>
                      </td>
                      <td>
                        <div style={{ maxWidth: 350 }}>
                          <div
                            className="truncate"
                            style={{ fontSize: 13 }}
                            title={q.question_text_de}
                          >
                            {q.question_text_de}
                          </div>
                          {q.change_markers && q.change_markers.length > 0 && (
                            <div className="flex gap-1" style={{ marginTop: 3 }}>
                              {q.change_markers.map((m: string) => (
                                <ChangeMarkerBadge key={m} marker={m} />
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`badge ${q.difficulty === 'Leicht' ? 'badge-success' : q.difficulty === 'Mittel' ? 'badge-warning' : 'badge-danger'}`}
                        >
                          {q.difficulty}
                        </span>
                      </td>
                      <td>
                        <QAStatusBadge status={q.qa_status} />
                      </td>
                      <td>
                        <div className="flex gap-1 items-center">
                          {Object.entries(LANG_FLAGS).map(([lang, flag]) => (
                            <span
                              key={lang}
                              title={`${flag} ${lang.toUpperCase()}: ${q.translations[lang] || 'N/A'}`}
                            >
                              <TranslationDot
                                status={(q.translations[lang] as any) || 'outdated'}
                              />
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        {q.has_image ? (
                          q.image_hidden ? (
                            <span
                              className="badge badge-danger"
                              style={{ fontSize: 10 }}
                              title="Bild versteckt"
                            >
                              🙈
                            </span>
                          ) : (
                            <span className="badge badge-success" style={{ fontSize: 10 }}>
                              <ImageIcon size={10} />
                            </span>
                          )
                        ) : (
                          <span className="badge badge-neutral" style={{ fontSize: 10 }}>
                            —
                          </span>
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-ghost btn-sm"
                          title="Öffnen"
                          onClick={() => onSelectQuestion(q.id)}
                        >
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
